import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CreditCard, Calendar, TrendingUp, AlertCircle } from 'lucide-react';
import { AuthContext } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Subscription() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      toast.error('Error al cargar la suscripción');
    } finally {
      setLoading(false);
    }
  };

  const getPlanDisplayName = (plan) => {
    const names = {
      free: 'Gratuito',
      starter: 'Starter',
      pro: 'Pro'
    };
    return names[plan] || plan;
  };

  const getPlanColor = (plan) => {
    const colors = {
      free: 'from-secondary to-white/20',
      starter: 'from-brand-meta to-brand-google',
      pro: 'from-ai-glow to-success'
    };
    return colors[plan] || colors.free;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-primary text-xl">Cargando suscripción...</div>
        </div>
      </DashboardLayout>
    );
  }

  const usagePercentage = {
    uploads: subscriptionStatus?.limits?.uploads !== 999999 
      ? (subscriptionStatus?.usage?.uploads / subscriptionStatus?.limits?.uploads) * 100 
      : 0,
    ai_analysis: subscriptionStatus?.limits?.ai_analysis !== 999999
      ? (subscriptionStatus?.usage?.ai_analysis / subscriptionStatus?.limits?.ai_analysis) * 100
      : 0
  };

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="subscription-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading font-bold text-4xl mb-2">Mi Suscripción</h1>
          <p className="text-secondary">Administra tu plan y uso</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Plan Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card p-8"
            data-testid="current-plan-card"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanColor(subscriptionStatus?.plan)} opacity-20 flex items-center justify-center`}>
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl">Plan Actual</h3>
                <p className="text-secondary text-sm">Tu plan activo</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline mb-2">
                <span className="font-heading font-bold text-4xl">{getPlanDisplayName(subscriptionStatus?.plan)}</span>
                {subscriptionStatus?.plan !== 'free' && (
                  <span className="ml-2 px-3 py-1 rounded-full bg-success/20 text-success text-xs font-medium">
                    {subscriptionStatus?.status}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-secondary">Estado</span>
                <span className="font-medium capitalize">{subscriptionStatus?.status}</span>
              </div>
              {subscriptionStatus?.plan !== 'free' && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-secondary">Próxima renovación</span>
                  <span className="font-medium">--</span>
                </div>
              )}
            </div>

            <Button
              onClick={() => navigate('/pricing')}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-full"
              data-testid="change-plan-button"
            >
              {subscriptionStatus?.plan === 'free' ? 'Mejorar Plan' : 'Cambiar Plan'}
            </Button>
          </motion.div>

          {/* Usage Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card p-8"
            data-testid="usage-card"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-brand-google/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-brand-google" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-xl">Uso del Plan</h3>
                <p className="text-secondary text-sm">Este mes</p>
              </div>
            </div>

            {/* Uploads Usage - removed */}
            {false &&
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-secondary">Uploads</span>
                <span className="text-sm font-medium">
                  {subscriptionStatus?.usage?.uploads} / {subscriptionStatus?.limits?.uploads === 999999 ? '∞' : subscriptionStatus?.limits?.uploads}
                </span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-brand-meta to-brand-google transition-all"
                  style={{ width: `${Math.min(usagePercentage.uploads, 100)}%` }}
                />
              </div>
            </div>

            }
            {/* AI Analysis Usage */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-secondary">Análisis IA</span>
                <span className="text-sm font-medium">
                  {subscriptionStatus?.usage?.ai_analysis} / {subscriptionStatus?.limits?.ai_analysis === 999999 ? '∞' : subscriptionStatus?.limits?.ai_analysis}
                </span>
              </div>
              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-ai-glow to-success transition-all"
                  style={{ width: `${Math.min(usagePercentage.ai_analysis, 100)}%` }}
                />
              </div>
            </div>

            {!subscriptionStatus?.can_upload && (
              <div className="flex items-start space-x-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-warning">Límite alcanzado</p>
                  <p className="text-xs text-warning/80 mt-1">Has alcanzado tu límite de uploads. Mejora tu plan para continuar.</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Plan Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-8 mt-6"
        >
          <h3 className="font-heading font-semibold text-xl mb-6">Características de tu plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="font-medium">Dashboard completo</p>
                <p className="text-xs text-secondary">Visualizaciones avanzadas</p>
              </div>
            </div>
            {subscriptionStatus?.plan !== 'free' && (
              <>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-meta/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-brand-meta" />
                  </div>
                  <div>
                    <p className="font-medium">Reportes exportables</p>
                    <p className="text-xs text-secondary">PDF y Excel</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-ai-glow/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-ai-glow" />
                  </div>
                  <div>
                    <p className="font-medium">IA ilimitada</p>
                    <p className="text-xs text-secondary">Análisis con GPT-4o</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
